'use client';

import { Language, LocalizedStringDto } from '@order-mage/integration-sdk';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { StorefrontAddonDef } from '@/api/menu-list/storefront-menu-snapshot.types';
import {
    buildPackageItemByIdMap,
    collectAddonMemberPackages,
    enrichPackageWithPriceableItem,
    getPriceValueTranslation,
    isPackageApplicable,
    resolveEffectiveAddons,
    shouldIncludePackages,
    useCartStore,
} from '@/entities/cart';
import type { CartMenuItem, MenuListsData } from '@/entities/cart/lib/item-price';
import { useStorefrontCartMenuData } from '@/entities/product/lib/useStorefrontMenu';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { Icon } from '@/shared/ui/icon';

type PackageItemById = ReturnType<typeof buildPackageItemByIdMap>;

type PackageComputeInput = {
    packagesSourceItem: Pick<CartMenuItem, 'packages' | 'packageBehaviours'>;
    cartAddons: Record<string, string | Record<string, number>> | undefined;
    itemAddons: StorefrontAddonDef[] | null | undefined;
    memberVariants: Record<string, string> | undefined;
    cartMenuData: MenuListsData | null | undefined;
    packageItemById: PackageItemById;
    deliveryMethodDefinitionId: string | null;
    userLanguage: Language;
    selectedCountry: Country;
    selectedCurrency: Currency;
};

const computePackages = ({
    packagesSourceItem,
    cartAddons,
    itemAddons,
    memberVariants,
    cartMenuData,
    packageItemById,
    deliveryMethodDefinitionId,
    userLanguage,
    selectedCountry,
    selectedCurrency,
}: PackageComputeInput) => {
    const enrichedPackages = (packagesSourceItem.packages ?? []).map((pkg) =>
        enrichPackageWithPriceableItem(pkg, packageItemById),
    );

    const mainItemPackages = shouldIncludePackages(
        packagesSourceItem,
        deliveryMethodDefinitionId ?? undefined,
    )
        ? enrichedPackages.filter((pkg) =>
              isPackageApplicable(pkg, deliveryMethodDefinitionId ?? undefined, userLanguage),
          )
        : [];

    const addonMemberPackages = collectAddonMemberPackages(
        cartAddons,
        cartMenuData ?? undefined,
        deliveryMethodDefinitionId ?? undefined,
        userLanguage,
        packageItemById,
        { addons: itemAddons, memberVariants },
    );

    const applicablePackages = [...mainItemPackages, ...addonMemberPackages];

    const packagesTotalPrice = applicablePackages.reduce((sum, pkg) => {
        if (!pkg.packageItem) return sum;
        let price = pkg.packageItem.prices?.find((p) => p.country === selectedCountry);
        if (!price) price = pkg.packageItem.prices?.find((p) => !p.country);
        if (!price) return sum;
        const priceValue = price.priceGroup.priceValues.find((pv) => pv.currency === selectedCurrency);
        if (!priceValue?.amount) return sum;
        const quantity = 'addonQuantity' in pkg ? Number(pkg.addonQuantity) : 1;
        return sum + parseFloat(priceValue.amount) * quantity;
    }, 0);

    return { applicablePackages, packagesTotalPrice };
};

type SharedProps = {
    packagesSourceItem: Pick<CartMenuItem, 'packages' | 'packageBehaviours'>;
    cartAddons: Record<string, string | Record<string, number>> | undefined;
    itemAddons: StorefrontAddonDef[] | null | undefined;
    memberVariants: Record<string, string> | undefined;
    packageItemById: PackageItemById;
    selectedCountry: Country;
    selectedCurrency: Currency;
    userLanguage: Language;
};

type PackageLineView = {
    packageItemId: string;
    name: LocalizedStringDto | undefined;
    amount: string;
    currency: Currency;
    quantity: number;
};

/** Package lines — re-renders only when this row's visible packages actually change. */
export const CartItemPackageLines = ({
    packagesSourceItem,
    cartAddons,
    itemAddons,
    memberVariants,
    packageItemById,
    selectedCountry,
    selectedCurrency,
    userLanguage,
}: SharedProps) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const cartMenuData = useStorefrontCartMenuData();
    const deliveryMethodDefinitionId = useCartStore(
        (s) => s.deliveryMethod?.deliveryMethodDefinitionId ?? null,
    );

    const lines = useMemo((): PackageLineView[] => {
        const { applicablePackages } = computePackages({
            packagesSourceItem,
            cartAddons,
            itemAddons,
            memberVariants,
            cartMenuData,
            packageItemById,
            deliveryMethodDefinitionId,
            userLanguage,
            selectedCountry,
            selectedCurrency,
        });

        return applicablePackages.flatMap((pkg) => {
            const packageItem = pkg.packageItem;
            if (!packageItem) return [];

            const quantity = 'addonQuantity' in pkg ? Number(pkg.addonQuantity) : 1;
            let price = packageItem.prices?.find((p) => p.country === selectedCountry);
            if (!price) price = packageItem.prices?.find((p) => !p.country);
            const priceValue = price?.priceGroup.priceValues.find((pv) => pv.currency === selectedCurrency);

            return [
                {
                    packageItemId: pkg.packageItemId,
                    name: packageItem.name as LocalizedStringDto | undefined,
                    amount: priceValue?.amount ?? '0',
                    currency: (priceValue?.currency as Currency) ?? selectedCurrency,
                    quantity,
                },
            ];
        });
    }, [
        packagesSourceItem,
        cartAddons,
        itemAddons,
        memberVariants,
        cartMenuData,
        packageItemById,
        deliveryMethodDefinitionId,
        userLanguage,
        selectedCountry,
        selectedCurrency,
    ]);

    return (
        <>
            {lines.map((line) => (
                <span
                    key={line.packageItemId}
                    className="cart-items-list-summary-box__meta"
                    data-testid="cart-item-packaging"
                    data-amount={line.amount}
                    data-currency={line.currency}
                    data-quantity={line.quantity}
                >
                    <Icon name="package" width={16} height={16} />
                    {line.name ? getLocalizedValue(line.name) : ''}{' '}
                    {line.amount !== '0' && (
                        <span>
                            {line.quantity < 0 ? '-' : ''}
                            {getPriceValueTranslation({
                                amount: parseFloat(line.amount),
                                currency: line.currency,
                                t,
                            })}
                            {Math.abs(line.quantity) > 1 && ` x ${Math.abs(line.quantity)}`}
                        </span>
                    )}
                </span>
            ))}
        </>
    );
};

type PriceProps = SharedProps & {
    hash: string;
    cartVariant: string | undefined;
};

type PriceView = {
    amount: number;
    currency: Currency;
};

/** Line total — re-renders only when this row's display amount/currency changes. */
export const CartItemPriceWithPackages = ({
    packagesSourceItem,
    cartAddons,
    memberVariants,
    packageItemById,
    selectedCountry,
    selectedCurrency,
    userLanguage,
    hash,
    cartVariant,
    itemAddons,
}: PriceProps) => {
    const t = useTranslations();
    const cartMenuData = useStorefrontCartMenuData();

    const priceView = useCartStore(
        useShallow((s): PriceView => {
            const deliveryMethodDefinitionId = s.deliveryMethod?.deliveryMethodDefinitionId ?? null;
            const { packagesTotalPrice } = computePackages({
                packagesSourceItem,
                cartAddons,
                itemAddons: cartMenuData?.items
                    ? resolveEffectiveAddons(cartMenuData.items, itemAddons, cartVariant, hash)
                    : undefined,
                memberVariants,
                cartMenuData,
                packageItemById,
                deliveryMethodDefinitionId,
                userLanguage,
                selectedCountry,
                selectedCurrency,
            });

            const finalPrice = s.getItemFinalPrice(
                { variant: cartVariant, addons: cartAddons, memberVariants },
                hash,
                itemAddons,
            );
            const itemPrices = s.getItemPrices(hash);

            return {
                amount: finalPrice ? parseFloat(finalPrice.amount) + packagesTotalPrice : packagesTotalPrice,
                currency:
                    (finalPrice?.currency as Currency) ??
                    (itemPrices?.itemPrice.currency as Currency) ??
                    selectedCurrency,
            };
        }),
    );

    return (
        <div className="cart-items-list-summary-box__price-wrapper">
            <strong className="cart-items-list-summary-box__price" data-testid="cart-item-price">
                {getPriceValueTranslation({
                    amount: priceView.amount,
                    currency: priceView.currency,
                    t,
                })}
            </strong>
        </div>
    );
};
